import React, { useState, useEffect, useRef } from 'react';
import {
    FileText, Printer, Search, RefreshCw,
    Loader2, AlertCircle, X, Building2,
    ChevronLeft, ChevronRight, Plus, FilePlus, Save, List
} from 'lucide-react';
import API_BASE_URL from "../config";
import { jsPDF } from 'jspdf';
import Swal from 'sweetalert2';

export default function Gatepass() {
    const [activeTab, setActiveTab] = useState('list');

    const [branchMRs, setBranchMRs] = useState([]);
    const [filteredMRs, setFilteredMRs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [selectedMR, setSelectedMR] = useState(null);
    const [selectedMRDetails, setSelectedMRDetails] = useState([]);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [showGatepass, setShowGatepass] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const printRef = useRef();

    const [truckSearch, setTruckSearch] = useState('');
    const [truckDropdownOpen, setTruckDropdownOpen] = useState(false);
    const [savedGatepasses, setSavedGatepasses] = useState([]);
    const [loadingSaved, setLoadingSaved] = useState(false);
    const [savedSearch, setSavedSearch] = useState('');
    const [savedPage, setSavedPage] = useState(1);
    const [showSavedModal, setShowSavedModal] = useState(false);
    const [selectedSaved, setSelectedSaved] = useState(null);
    const savedPrintRef = useRef();

    const generateWbRef = () => {
        const now = new Date();
        const year = now.getFullYear();
        const date = String(now.getMonth() + 1).padStart(2, '0') + String(now.getDate()).padStart(2, '0');
        const timestamp = String(now.getHours()).padStart(2, '0') +
            String(now.getMinutes()).padStart(2, '0') +
            String(now.getSeconds()).padStart(2, '0');
        return `WB-${year}-${date}-${timestamp}`;
    };

    const [manualForm, setManualForm] = useState({
        customerName: '',
        customerAddress: '',
        wbRef: generateWbRef(),
        deliveryDate: new Date().toISOString().split('T')[0],
        expectedArrivalDate: '',
        truckPlateNo: '',
        requestedBy: '',
        preparedBy: '',
        verifiedBy: '',
        dispatchBy: '',
        items: [{ code: '', description: '', remarks: '', qty: '', unit: '', packaging: '' }]
    });
    const [trucks, setTrucks] = useState([]);
    const [users, setUsers] = useState([]);
    const [loadingFormData, setLoadingFormData] = useState(false);

    const statusLabels = {
        0: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
        1: { label: 'For Approval', color: 'bg-yellow-100 text-yellow-700' },
        2: { label: 'Approved', color: 'bg-green-100 text-green-700' },
        3: { label: 'Released', color: 'bg-blue-100 text-blue-700' },
        4: { label: 'Rejected', color: 'bg-red-100 text-red-700' },
    };

    useEffect(() => { fetchBranchMRs(); fetchFormData(); fetchSavedGatepasses(); }, []);

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (userData) {
            try {
                const user = JSON.parse(userData);
                const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
                setManualForm(prev => ({ ...prev, requestedBy: fullName, preparedBy: fullName }));
            } catch { }
        }
    }, []);

    useEffect(() => {
        const filtered = branchMRs.filter(mr => {
            const search = searchText.toLowerCase();
            return (
                mr.mrNo?.toLowerCase().includes(search) ||
                mr.controlNo?.toLowerCase().includes(search) ||
                mr.giNumber?.toLowerCase().includes(search) ||
                mr.gi_Number?.toLowerCase().includes(search) ||
                mr.requestedByName?.toLowerCase().includes(search) ||
                mr.branchSpecify?.toLowerCase().includes(search) ||
                mr.departmentName?.toLowerCase().includes(search)
            );
        });
        setFilteredMRs(filtered);
        setCurrentPage(1);
    }, [searchText, branchMRs]);

    const fetchBranchMRs = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_BASE_URL}api/MRHeader`);
            if (!res.ok) throw new Error('Failed to fetch MR headers');
            const data = await res.json();
            const branchOnly = data.filter(mr =>
                mr.department === 'Branch' || mr.departmentName?.toLowerCase().includes('branch')
            );
            setBranchMRs(branchOnly);
            setFilteredMRs(branchOnly);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchSavedGatepasses = async () => {
        setLoadingSaved(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/Gatepass`);
            if (res.ok) {
                const data = await res.json();
                setSavedGatepasses(data);
            }
        } catch (err) {
            console.error('Failed to fetch saved gatepasses:', err);
        } finally {
            setLoadingSaved(false);
        }
    };

    const fetchFormData = async () => {
        setLoadingFormData(true);
        try {
            const [trucksRes, usersRes] = await Promise.all([
                fetch(`${API_BASE_URL}api/Truck`),
                fetch(`${API_BASE_URL}api/Users`)
            ]);
            if (trucksRes.ok) {
                const data = await trucksRes.json();
                setTrucks(data.map(t => ({
                    id: t.id,
                    truckPlate: t.truckPlate || t.TruckPlate,
                    model: t.model || t.Model,
                })));
            }
            if (usersRes.ok) {
                const data = await usersRes.json();
                setUsers(data.filter(u => u.isActive === 1));
            }
        } catch (err) {
            console.error('Failed to fetch form data:', err);
        } finally {
            setLoadingFormData(false);
        }
    };

    const getGINumber = (mr) => mr?.giNumber || mr?.gi_Number || mr?.GI_Number || '—';

    const handleViewGatepass = async (mr) => {
        setSelectedMR(mr);
        setLoadingDetails(true);
        setShowGatepass(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/MRDetails/by-header/${mr.id}`);
            if (!res.ok) throw new Error('Failed to fetch details');
            const details = await res.json();
            setSelectedMRDetails(details);
        } catch {
            setSelectedMRDetails([]);
        } finally {
            setLoadingDetails(false);
        }
    };

    const handlePrint = () => {
        const printContents = printRef.current.innerHTML;
        const originalBody = document.body.innerHTML;
        document.body.innerHTML = `
                <html><head><style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { font-family: Arial, sans-serif; font-size: 11px; background: #fff; }
                    table { border-collapse: collapse; width: 100%; }
                    td, th { border: 1px solid #000; padding: 4px 6px; }
                    @media print { @page { margin: 10mm; size: A4; } }
                </style></head><body>${printContents}</body></html>
            `;
        window.print();
        document.body.innerHTML = originalBody;
        window.location.reload();
    };

    const handlePrintSaved = () => {
        const gp = selectedSaved;
        if (!gp) return;

        const printWindow = window.open('', '_blank');

        const today = new Date();
        const effectivityDate = today.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase();
        const deliveryDateStr = gp.deliveryDate
            ? new Date(gp.deliveryDate).toLocaleDateString('en-US')
            : '';

        const minRows = 4;
        const items = gp.items || [];
        const blankRowsNeeded = Math.max(0, minRows - items.length);
        const allRows = [
            ...items,
            ...Array.from({ length: blankRowsNeeded }).map(() => ({}))
        ];

        const itemRows = allRows.map(item => `
            <tr>
                <td style="text-align:center;">${item.qty || ''}</td>
                <td style="text-align:center;">${item.unit || ''}</td>
    <td style="text-transform:uppercase;text-align:center;">${item.description || ''}</td>
                <td style="text-align:center;">${item.remarks || ''}</td>
                <td style="text-align:center;">${item.packaging || ''}</td>
            </tr>
        `).join('');

        printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
    <meta charset="UTF-8">
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }

        body {
            font-family: Arial, sans-serif;
            font-size: 10px;
            background: #fff;
            color: #000;
            padding: 0;
        }

        /* ── REMOVE browser date/time/URL headers & footers ── */
        @media print {
            @page {
                size: A4 portrait;
                /* Setting all margins to 0 removes the browser header/footer area */
                margin: 0;
            }
            body {
                /* Re-apply padding inside the body so content has breathing room */
                padding: 12mm 14mm;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }
            .page-border { border: 1.5px solid #000; }
        }

        /* Screen preview padding */
        .page-wrapper {
            padding: 14px 16px;
        }

        /* ── OUTER BORDER around the whole document ── */
        .page-border {
            border: 1.5px solid #000;
            padding: 10px;
            width: 100%;
        }

        /* ── HEADER TABLE ── */
        .header-table {
            width: 100%;
            border-collapse: collapse;
        }
        .header-table td,
        .header-table th {
            border: 1px solid #000;
            padding: 3px 5px;
            vertical-align: middle;
        }

        .company-name {
            font-size: 12px;
            font-style: italic;
            font-weight: bold;
            display: block;
            margin-bottom: 2px;
        }
        .company-address { font-size: 7.5px; }
        .forms-label { text-align: center; font-weight: bold; font-size: 8.5px; }
        .doc-code-label { text-align: center; font-size: 7.5px; font-weight: bold; padding: 2px 4px; }
        .doc-code-value { text-align: center; font-size: 8.5px; padding: 1px 4px 3px; }
        .gatepass-title { font-size: 20px; font-weight: bold; letter-spacing: 2px; }
        .doc-owner-label { font-size: 7.5px; font-weight: bold; }
        .doc-owner-value { font-size: 8.5px; }

        .sub-table { width: 100%; border-collapse: collapse; }
        .sub-table td, .sub-table th {
            border: 1px solid #000;
            padding: 2px 4px;
            text-align: center;
            font-size: 7.5px;
        }
        .sub-table th { font-weight: bold; }

        /* ── CONTROL NO ── */
        .control-no {
            padding: 5px 2px;
            font-size: 10px;
            margin-bottom: 3px;
        }
        .control-no-value { color: #dc2626; font-weight: bold; }

        /* ── INFO TABLE ── */
        .info-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 4px;
        }
        .info-table td {
            border: 1px solid #000;
            padding: 3px 6px;
            font-size: 10px;
        }
        .info-label { width: 70px; font-weight: bold; white-space: nowrap; }

        /* ── ITEMS TABLE ── */
        .items-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 4px;
            table-layout: fixed;
        }
        .items-table th, .items-table td {
            border: 1px solid #000;
            padding: 3px 4px;
            font-size: 10px;
        }
        .items-table thead th {
            text-align: center;
            font-weight: bold;
            font-size: 9px;
            background: #f1f5f9;
        }
        .col-qty   { width: 7%; }
        .col-unit  { width: 9%; }
        .col-desc  { width: 44%; }
        .col-rmk   { width: 25%; }
        .col-pcbox { width: 15%; }
        .items-table tbody td { height: 18px; }
        .nothing-follows { text-align: center; font-weight: bold; font-size: 9.5px; }

        /* ── SIGNATURES ── */
        .sig-table { width: 100%; border-collapse: collapse; margin-top: 10px; }
        .sig-table td { border: none; padding: 4px 0; font-size: 10px; vertical-align: bottom; }
        .sig-label { width: 100px; font-weight: bold; white-space: nowrap; }
        .sig-line-wrap { width: 320px; }
    .sig-name {
        font-weight: bold;
        font-size: 10px;
        min-height: 18px;
        padding-bottom: 1px;
        border-bottom: 1px solid #000;
        text-align: center;   /* ADD THIS */
    }
        .sig-hint { font-size: 7.5px; color: #555; text-align: center; padding-top: 1px; }

        /* ── FOOTER ── */
        .footer {
            margin-top: 18px;
            border-top: 1px solid #000;
            padding-top: 5px;
            font-size: 7.5px;
            color: #444;
            font-style: italic;
            text-align: center;
        }
    </style>
    </head>
    <body>
    <div class="page-wrapper">
    <div class="page-border">

        <!-- HEADER -->
        <table class="header-table">
            <tr>
                <td style="width:34%;vertical-align:middle;" rowspan="2">
                    <span class="company-name">MEGA SOFT Hygienic Products, Inc.</span>
                    <span class="company-address">38 Pleasant Drive, Pleasant View Subd., Novaliches Caloocan City</span>
                </td>
                <td style="width:20%;text-align:center;">
                    <span class="forms-label">FORMS AND CHECKLIST</span>
                </td>
                <td style="width:46%;text-align:center;vertical-align:middle;" rowspan="2">
                    <span class="gatepass-title">GATEPASS</span>
                </td>
            </tr>
            <tr>
                <td style="padding:0;">
                    <div class="doc-code-label">DOCUMENT CODE</div>
                    <div class="doc-code-value">SPR-F-04-DP-003</div>
                </td>
            </tr>
            <tr>
                <td style="vertical-align:middle;">
                    <div class="doc-owner-label">DOCUMENT OWNER</div>
                    <div class="doc-owner-value">SPAREPARTS DEPARTMENT</div>
                </td>
                <td style="padding:0;vertical-align:top;">
                    <table class="sub-table">
                        <tr>
                            <th>ISSUE NO</th>
                            <th>REVISION NO</th>
                            <th>REVISION DATE</th>
                        </tr>
                        <tr>
                            <td>1</td>
                            <td>0</td>
                            <td>NONE</td>
                        </tr>
                    </table>
                </td>
                <td style="padding:0;vertical-align:top;">
                    <table class="sub-table">
                        <tr>
                            <th>EFFECTIVITY DATE</th>
                            <th>NO. OF PAGES</th>
                            <th>PAGE</th>
                        </tr>
                        <tr>
                            <td>${effectivityDate}</td>
                            <td>1</td>
                            <td>1</td>
                        </tr>
                    </table>
                </td>
            </tr>
        </table>

        <!-- CONTROL NO -->
    <div class="control-no" style="display:flex; justify-content:space-between; align-items:center;">
        <div>
            <strong>CONTROL NO. </strong>
            <span class="control-no-value">${gp.wbRef || '—'}</span>
        </div>
        <div>
            <strong>PLATE NO. </strong>
            <span style="font-weight:bold; color:#1d4ed8;">${gp.truckPlateNo || '—'}</span>
        </div>
    </div>

        <!-- DATE / FROM / TO / C/O -->
        <table class="info-table">
            <tr>
                <td class="info-label">Date:</td>
                <td>${deliveryDateStr}</td>
            </tr>
            <tr>
                <td class="info-label">From:</td>
            <td>MEGA SOFT HYGIENIC PRODUCT INC</td>

            </tr>
            <tr>
                <td class="info-label">To:</td>
                <td>${gp.customerName || ''}</td>
            </tr>
            <tr>
                <td class="info-label">Attention To</td>
                <td>${gp.customerAddress || ''}</td>
            </tr>
        </table>

        <!-- ITEMS -->
        <table class="items-table">
            <thead>
                <tr>
                    <th class="col-qty">Qty</th>
                    <th class="col-unit">Units</th>
                    <th class="col-desc" style="text-align:center;padding-left:6px;">Description</th>
                    <th class="col-rmk">Remarks</th>
                    <th class="col-pcbox">Pc/Box</th>
                </tr>
            </thead>
            <tbody>
                ${itemRows}
                <tr>
                    <td colspan="5" class="nothing-follows">****NOTHING FOLLOWS****</td>
                </tr>
            </tbody>
        </table>

        <!-- SIGNATURES -->
        <table class="sig-table">
            <tr>
                <td class="sig-label">Prepared By:</td>
                <td class="sig-line-wrap">
                    <div class="sig-name">${gp.preparedBy || ''}</div>
                    <div class="sig-hint">Printed Name &amp; Date</div>
                </td>
            </tr>
            <tr>
                <td class="sig-label">Endorsed To:</td>
                <td class="sig-line-wrap">
                    <div class="sig-name">${gp.verifiedBy || ''}</div>
                    <div class="sig-hint">Printed Name &amp; Date</div>
                </td>
            </tr>
            <tr>
                <td class="sig-label">Received By:</td>
                <td class="sig-line-wrap">
                <div class="sig-name">${gp.dispatchBy || ''}</div>

                    <div class="sig-hint">Printed Name &amp; Date</div>
                </td>
            </tr>
        </table>

        <!-- FOOTER -->
        <div class="footer">
            This document is a property of Mega Soft. Unauthorized copying, reproduction, distribution, or alteration is strictly prohibited.
        </div>

    </div>
    </div>
    </body>
    </html>
        `);

        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
            printWindow.print();
            printWindow.close();
        }, 600);
    };

    const handleManualChange = (e) => {
        const { name, value } = e.target;
        setManualForm(prev => ({ ...prev, [name]: value }));
    };

    const handleItemChange = (index, field, value) => {
        setManualForm(prev => {
            const items = [...prev.items];
            items[index] = { ...items[index], [field]: value };
            return { ...prev, items };
        });
    };

    const addItem = () => {
        if (manualForm.items.length >= 20) {
            Swal.fire({
                icon: 'warning',
                title: 'Maximum Items Reached',
                text: 'Only 20 items allowed per gatepass. Please create a new gatepass for additional items.',
                timer: 2500,
                showConfirmButton: false
            });
            return;
        }
        setManualForm(prev => ({
            ...prev,
            items: [...prev.items, { code: '', description: '', remarks: '', qty: '', unit: '', packaging: '' }]
        }));
    };
    const removeItem = (index) => {
        setManualForm(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const handleTruckSelect = (e) => {
        const truck = trucks.find(t => t.id === parseInt(e.target.value));
        setManualForm(prev => ({ ...prev, truckPlateNo: truck ? truck.truckPlate : '' }));
    };

    const saveManualGatepass = async () => {
        try {
            const payload = {
                customerName: manualForm.customerName,
                customerAddress: manualForm.customerAddress,
                wbRef: manualForm.wbRef,
                deliveryDate: manualForm.deliveryDate || null,
                expectedArrivalDate: manualForm.expectedArrivalDate || null,
                truckPlateNo: manualForm.truckPlateNo,
                requestedBy: manualForm.requestedBy,
                preparedBy: manualForm.preparedBy,
                verifiedBy: manualForm.verifiedBy,
                dispatchBy: manualForm.dispatchBy,
                status: "Draft",
                items: manualForm.items
                    .filter(i => i.description || i.qty)
                    .map(i => ({
                        code: i.code || null,
                        description: i.description || null,
                        remarks: i.remarks || null,
                        unit: i.unit || null,
                        qty: i.qty || null,
                        packaging: i.packaging || null
                    }))
            };

            const res = await fetch(`${API_BASE_URL}api/Gatepass`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!res.ok) throw new Error('Failed to save');
            const saved = await res.json();
            await fetchSavedGatepasses();
            Swal.fire({
                icon: 'success',
                title: 'Gatepass Saved!',
                text: `Gatepass #${saved.id} saved successfully.`,
                timer: 2000,
                showConfirmButton: false
            });
        } catch (err) {
            console.error(err);
            Swal.fire({
                icon: 'error',
                title: 'Save Failed',
                text: err.message || 'Something went wrong.'
            });
        }
    };

    const exportManualToPDF = () => {
        const doc = new jsPDF({ unit: 'mm', format: 'a4' });
        const pageW = 210;
        let y = 15;

        doc.setFontSize(13);
        doc.setFont(undefined, 'bold');
        doc.text('MEGA SOFT HYGIENIC PRODUCT INC.', pageW / 2, y, { align: 'center' });
        y += 6;
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        doc.text('38 - E Pleasant Drive, Pleasant View Subd., Libis, Caloocan City', pageW / 2, y, { align: 'center' });
        y += 5;
        doc.text('Telefax : 984-10-01', pageW / 2, y, { align: 'center' });
        y += 6;
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('GATEPASS', pageW / 2, y, { align: 'center' });
        y += 4;
        doc.setLineWidth(0.5);
        doc.line(14, y, 196, y);
        y += 7;

        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('DELIVERY TO', 14, y);
        y += 5;

        doc.setFont(undefined, 'bold');
        doc.setFontSize(8);
        doc.text('CUSTOMER NAME', 14, y);
        doc.text(':', 44, y);
        doc.setFont(undefined, 'normal');
        doc.text(manualForm.customerName || '', 47, y);
        doc.line(47, y + 1, 130, y + 1);
        doc.setFont(undefined, 'bold');
        doc.text('WBREF:', 140, y);
        doc.setFont(undefined, 'normal');
        doc.text(manualForm.wbRef || '', 157, y);
        doc.line(157, y + 1, 196, y + 1);
        y += 7;

        doc.setFont(undefined, 'bold');
        doc.text('CUSTOMER ADDRESS', 14, y);
        doc.text(':', 44, y);
        doc.setFont(undefined, 'normal');
        doc.text(manualForm.customerAddress || '', 47, y);
        doc.line(47, y + 1, 196, y + 1);
        y += 9;

        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('DELIVERY INSTRUCTION', 14, y);
        y += 5;

        const dlRows = [
            ['DELIVERY DATE', manualForm.deliveryDate],
            ['EXPECTED ARRIVAL DATE', manualForm.expectedArrivalDate],
            ['TRUCK PLATE NO.', manualForm.truckPlateNo],
        ];
        dlRows.forEach(([label, val]) => {
            doc.setFontSize(8);
            doc.setFont(undefined, 'bold');
            doc.text(label, 14, y);
            doc.setFont(undefined, 'normal');
            doc.text(val || '', 70, y);
            doc.line(70, y + 1, 130, y + 1);
            y += 6;
        });

        y += 2;
        doc.setFont(undefined, 'bold');
        doc.setFontSize(8);
        doc.text('Request by:', 14, y);
        doc.setFont(undefined, 'normal');
        doc.text(manualForm.requestedBy || '', 38, y);
        doc.line(38, y + 1, 130, y + 1);
        y += 10;

        // FIX: 6 columns with unit
        const colWidths = [20, 20, 55, 35, 15, 37];
        const headers = ['CODE', 'UNITS', 'ITEM DESCRIPTION', 'REMARKS', 'QTY', 'PACKAGING'];
        const tableX = 14;
        const tableW = colWidths.reduce((a, b) => a + b, 0);

        doc.setFillColor(30, 64, 175);
        doc.rect(tableX, y, tableW, 7, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont(undefined, 'bold');
        doc.setFontSize(8);
        let cx = tableX;
        headers.forEach((h, i) => {
            doc.text(h, cx + colWidths[i] / 2, y + 5, { align: 'center' });
            cx += colWidths[i];
        });
        doc.setTextColor(0, 0, 0);
        y += 7;

        const rowH = 7;
        const allItems = [
            ...manualForm.items,
            ...Array.from({ length: Math.max(0, 5 - manualForm.items.length) }).map(() => ({
                code: '', unit: '', description: '', remarks: '', qty: '', packaging: ''
            }))
        ];

        allItems.forEach((item) => {
            cx = tableX;
            doc.setFont(undefined, 'normal');
            doc.setFontSize(8);
            // FIX: include unit in cells
            const cells = [item.code, item.unit, item.description, item.remarks, item.qty, item.packaging];
            cells.forEach((val, i) => {
                doc.rect(cx, y, colWidths[i], rowH);
                doc.text(String(val || ''), cx + 2, y + 5);
                cx += colWidths[i];
            });
            y += rowH;
        });

        y += 10;
        const sigBoxW = 58;
        const sigLabels = ['Prepared by:', 'Verified By:', 'Dispatch by:'];
        const sigValues = [manualForm.preparedBy, manualForm.verifiedBy, manualForm.dispatchBy];
        const sigXs = [14, 76, 138];

        sigLabels.forEach((label, i) => {
            doc.setFont(undefined, 'bold');
            doc.setFontSize(8);
            doc.text(label, sigXs[i] + sigBoxW / 2, y, { align: 'center' });
            doc.setFont(undefined, 'normal');
            doc.text(sigValues[i] || '', sigXs[i] + sigBoxW / 2, y + 14, { align: 'center' });
            doc.line(sigXs[i], y + 15, sigXs[i] + sigBoxW, y + 15);
            doc.setFontSize(7);
            doc.setTextColor(100);
            if (i === 2) doc.text('Signature over Printed Name/Date', sigXs[i] + sigBoxW / 2, y + 19, { align: 'center' });
            doc.setTextColor(0);
        });

        y += 24;
        doc.setFont(undefined, 'bold');
        doc.setFontSize(9);
        doc.text('CUSTOMERS ACKNOWLEDGMENT:', 14, y);
        y += 6;
        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        doc.text('Customer Representative :', 14, y);
        doc.line(65, y + 1, 140, y + 1);
        doc.setTextColor(100);
        doc.setFontSize(7);
        doc.text('Signature over Printed Name/Date', 145, y);
        doc.setTextColor(0);
        y += 7;
        doc.setFontSize(8);
        doc.text('Status of Delivered Items :', 14, y);
        doc.line(65, y + 1, 140, y + 1);
        y += 10;
        doc.setFontSize(7);
        doc.setTextColor(80);
        doc.text('2-Customer Copy/1-Logistics Copy/-1File Copy', 14, y);
        doc.text(`Generated on ${new Date().toLocaleString()}`, pageW - 14, y, { align: 'right' });

        doc.save(`Gatepass_Manual_${manualForm.wbRef || new Date().getTime()}.pdf`);
    };

    const totalPages = Math.ceil(filteredMRs.length / itemsPerPage);
    const startIdx = (currentPage - 1) * itemsPerPage;
    const currentMRs = filteredMRs.slice(startIdx, startIdx + itemsPerPage);

    const formattedDate = selectedMR
        ? new Date(selectedMR.mrDate).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })
        : '';

    const filteredSaved = savedGatepasses.filter(g => {
        const s = savedSearch.toLowerCase();
        return (
            g.wbRef?.toLowerCase().includes(s) ||
            g.customerName?.toLowerCase().includes(s) ||
            g.preparedBy?.toLowerCase().includes(s) ||
            g.status?.toLowerCase().includes(s)
        );
    });


    // ── DRAFT ──────────────────────────────────────────────
    // NEW — i-replace ng ganito
    // ── DRAFT ──────────────────────────────────────────────
    const DRAFTS_KEY = 'gatepass_drafts';
    const [showDraftModal, setShowDraftModal] = useState(false);
    const [drafts, setDrafts] = useState(() => {
        try { return JSON.parse(localStorage.getItem(DRAFTS_KEY) || '[]'); }
        catch { return []; }
    });

    const saveDraft = () => {
        const draftName = `Draft — ${manualForm.customerName || 'Unnamed'} (${new Date().toLocaleTimeString()})`;
        const newDraft = { id: Date.now(), name: draftName, savedAt: new Date().toISOString(), form: manualForm };
        const updated = [...drafts, newDraft];
        localStorage.setItem(DRAFTS_KEY, JSON.stringify(updated));
        setDrafts(updated);
        Swal.fire({ icon: 'success', title: 'Draft Saved!', text: draftName, timer: 1800, showConfirmButton: false });
    };

    const loadDraft = (draft) => {
        setManualForm(draft.form);
        setShowDraftModal(false);
        Swal.fire({ icon: 'success', title: 'Draft Loaded!', timer: 1200, showConfirmButton: false });
    };

    const deleteDraft = (id) => {
        const updated = drafts.filter(d => d.id !== id);
        localStorage.setItem(DRAFTS_KEY, JSON.stringify(updated));
        setDrafts(updated);
        if (updated.length === 0) setShowDraftModal(false);
    };

    const clearAllDrafts = () => {
        localStorage.removeItem(DRAFTS_KEY);
        setDrafts([]);
        setShowDraftModal(false);
        setManualForm({
            customerName: '', customerAddress: '', wbRef: generateWbRef(),
            deliveryDate: new Date().toISOString().split('T')[0], expectedArrivalDate: '',
            truckPlateNo: '', requestedBy: '', preparedBy: '', verifiedBy: '', dispatchBy: '',
            items: [{ code: '', description: '', remarks: '', qty: '', unit: '', packaging: '' }]
        });
        Swal.fire({ icon: 'success', title: 'All Drafts Cleared!', timer: 1200, showConfirmButton: false });
    };


    const savedTotalPages = Math.ceil(filteredSaved.length / itemsPerPage);
    const savedStartIdx = (savedPage - 1) * itemsPerPage;
    const currentSaved = filteredSaved.slice(savedStartIdx, savedStartIdx + itemsPerPage);


    // ── AUTO-PROMPT kung may draft pag load ng tab ──
    useEffect(() => {
        if (activeTab === 'create') {
            const saved = JSON.parse(localStorage.getItem(DRAFTS_KEY) || '[]');
            setDrafts(saved);
        }
    }, [activeTab]);
    // ── SAVED GATEPASS DOCUMENT ──
    const SavedGatepassDocument = ({ gp }) => {
        if (!gp) return null;
        const today = new Date();
        const effectivityDate = today.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase();




        return (
            <div ref={savedPrintRef} style={{
                width: '750px', margin: '0 auto', fontFamily: 'Arial, sans-serif',
                fontSize: '10px', color: '#000', background: '#fff', padding: '10px'
            }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '0' }}>
                    <tbody>
                        <tr>
                            <td rowSpan={2} style={{ border: '1px solid #000', padding: '8px 12px', width: '35%', verticalAlign: 'middle' }}>
                                <div style={{ fontWeight: 'bold', fontSize: '13px', fontStyle: 'italic' }}>MEGA SOFT Hygienic Products, Inc.</div>
                                <div style={{ fontSize: '8px', marginTop: '2px', color: '#444' }}>
                                    38 Pleasant Drive, Pleasant View Subd., Novaliches Caloocan City
                                </div>
                            </td>
                            <td style={{ border: '1px solid #000', padding: '4px 8px', width: '35%', textAlign: 'center', fontSize: '9px', fontWeight: 'bold' }}>
                                FORMS AND CHECKLIST
                            </td>
                            <td rowSpan={2} style={{ border: '1px solid #000', padding: '8px', width: '30%', textAlign: 'center', verticalAlign: 'middle' }}>
                                <div style={{ fontWeight: 'bold', fontSize: '18px', letterSpacing: '2px' }}>GATEPASS</div>
                            </td>
                        </tr>
                        <tr>
                            <td style={{ border: '1px solid #000', padding: '4px 8px', textAlign: 'center' }}>
                                <div style={{ fontSize: '8px', fontWeight: 'bold' }}>DOCUMENT CODE</div>
                                <div style={{ fontSize: '9px' }}>SPR-F-04-DP-003</div>
                            </td>
                        </tr>
                        <tr>
                            <td style={{ border: '1px solid #000', padding: '4px 8px' }}>
                                <div style={{ fontSize: '8px', fontWeight: 'bold' }}>DOCUMENT OWNER</div>
                                <div style={{ fontSize: '9px' }}>SPAREPARTS DEPARTMENT</div>
                            </td>
                            <td style={{ border: '1px solid #000', padding: '0' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ border: '1px solid #000', padding: '3px 6px', textAlign: 'center', fontSize: '8px', fontWeight: 'bold' }}>ISSUE NO</td>
                                            <td style={{ border: '1px solid #000', padding: '3px 6px', textAlign: 'center', fontSize: '8px', fontWeight: 'bold' }}>REVISION NO</td>
                                            <td style={{ border: '1px solid #000', padding: '3px 6px', textAlign: 'center', fontSize: '8px', fontWeight: 'bold' }}>REVISION DATE</td>
                                        </tr>
                                        <tr>
                                            <td style={{ border: '1px solid #000', padding: '3px 6px', textAlign: 'center', fontSize: '9px' }}>1</td>
                                            <td style={{ border: '1px solid #000', padding: '3px 6px', textAlign: 'center', fontSize: '9px' }}>0</td>
                                            <td style={{ border: '1px solid #000', padding: '3px 6px', textAlign: 'center', fontSize: '9px' }}>NONE</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                            <td style={{ border: '1px solid #000', padding: '0' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ border: '1px solid #000', padding: '3px 6px', textAlign: 'center', fontSize: '8px', fontWeight: 'bold' }}>EFFECTIVITY DATE</td>
                                            <td style={{ border: '1px solid #000', padding: '3px 6px', textAlign: 'center', fontSize: '8px', fontWeight: 'bold' }}>NO. OF PAGES</td>
                                            <td style={{ border: '1px solid #000', padding: '3px 6px', textAlign: 'center', fontSize: '8px', fontWeight: 'bold' }}>PAGE</td>
                                        </tr>
                                        <tr>
                                            <td style={{ border: '1px solid #000', padding: '3px 6px', textAlign: 'center', fontSize: '9px' }}>{effectivityDate}</td>
                                            <td style={{ border: '1px solid #000', padding: '3px 6px', textAlign: 'center', fontSize: '9px' }}>1</td>
                                            <td style={{ border: '1px solid #000', padding: '3px 6px', textAlign: 'center', fontSize: '9px' }}>1</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div style={{ padding: '6px 4px', fontSize: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <span style={{ fontWeight: 'bold' }}>CONTROL NO. </span>
                        <span style={{ fontWeight: 'bold', color: '#dc2626' }}>{gp.wbRef || '—'} </span>
                    </div>
                    <div>
                        <span style={{ fontWeight: 'bold' }}>PLATE NO. </span>
                        <span style={{ fontWeight: 'bold', color: '#1d4ed8' }}>{gp.truckPlateNo || '—'}</span>
                    </div>
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '4px' }}>
                    <tbody>
                        {[
                            { label: 'Date:', value: gp.deliveryDate ? new Date(gp.deliveryDate).toLocaleDateString('en-US') : '' },
                            { label: 'From:', value: 'MEGA SOFT HYGIENIC PRODUCT INC' },

                            { label: 'To:', value: gp.customerName || '' },
                            { label: 'Attention To:', value: gp.customerAddress || '' },
                        ].map((row, i) => (
                            <tr key={i}>
                                <td style={{ border: '1px solid #000', padding: '4px 8px', width: '80px', fontWeight: 'bold', fontSize: '10px' }}>{row.label}</td>
                                <td style={{ border: '1px solid #000', padding: '4px 8px', fontSize: '10px' }}>{row.value}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* ITEMS TABLE — FIX: 5 columns only, no item.code column */}
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '4px' }}>
                    <thead>
                        <tr>
                            {[
                                { label: 'Qty', w: '8%' },
                                { label: 'Units', w: '10%' },
                                { label: 'Description', w: '42%' },
                                { label: 'Remarks', w: '25%' },
                                { label: 'Pc/Box', w: '15%' },
                            ].map((h, i) => (
                                <th key={i} style={{
                                    border: '1px solid #000', padding: '5px 6px',
                                    textAlign: 'center', fontWeight: 'bold',
                                    fontSize: '10px', width: h.w, background: '#f1f5f9'
                                }}>{h.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {gp.items && gp.items.length > 0 ? gp.items.map((item, idx) => (
                            <tr key={idx}>
                                <td style={{ border: '1px solid #000', padding: '4px 6px', textAlign: 'center', fontSize: '10px', fontWeight: 'bold' }}>{item.qty || ''}</td>
                                {/* FIX: item.unit — hindi item.code */}
                                <td style={{ border: '1px solid #000', padding: '4px 6px', textAlign: 'center', fontSize: '10px', fontWeight: 'bold' }}>{item.unit || ''}</td>
                                <td style={{ border: '1px solid #000', padding: '4px 6px', fontSize: '10px', textTransform: 'uppercase', textAlign: 'center' }}>
                                    {item.description || ''}</td>
                                <td style={{ border: '1px solid #000', padding: '4px 6px', textAlign: 'center', fontSize: '10px' }}>{item.remarks || ''}</td>
                                <td style={{ border: '1px solid #000', padding: '4px 6px', textAlign: 'center', fontSize: '10px', fontWeight: 'bold' }}>{item.packaging || ''}</td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={5} style={{ border: '1px solid #000', padding: '10px', textAlign: 'center', color: '#999' }}>No items</td>
                            </tr>
                        )}
                        {Array.from({ length: Math.max(0, 4 - (gp.items?.length || 0)) }).map((_, i) => (
                            <tr key={`empty-${i}`}>
                                {[...Array(5)].map((_, j) => (
                                    <td key={j} style={{ border: '1px solid #000', padding: '4px 6px', height: '22px' }}>&nbsp;</td>
                                ))}
                            </tr>
                        ))}
                        <tr>
                            <td colSpan={5} style={{ border: '1px solid #000', padding: '4px', textAlign: 'center', fontWeight: 'bold', fontSize: '10px' }}>
                                ****NOTHING FOLLOWS****
                            </td>
                        </tr>
                    </tbody>
                </table>

                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '8px' }}>
                    <tbody>
                        {[
                            { label: 'Prepared By:', value: gp.preparedBy },
                            { label: 'Endorsed To:', value: gp.verifiedBy },
                            { label: 'Received By:', value: gp.dispatchBy || '' },

                        ].map((sig, i) => (
                            <tr key={i}>
                                <td style={{ border: 'none', padding: '6px 8px', width: '120px', fontWeight: 'bold', fontSize: '10px', verticalAlign: 'bottom' }}>
                                    {sig.label}
                                </td>
                                <td style={{ padding: '6px 8px', verticalAlign: 'bottom', width: '300px' }}>
                                    <div style={{ borderBottom: '1px solid #000', minWidth: '220px', minHeight: '22px', paddingBottom: '2px', fontSize: '10px', fontWeight: 'bold', textAlign: 'center' }}>
                                        {sig.value || ''}
                                    </div>
                                    <div style={{ fontSize: '8px', color: '#555', textAlign: 'center', marginTop: '2px' }}>Printed Name & Date</div>
                                </td>
                                <td style={{ border: 'none' }}>&nbsp;</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div style={{
                    marginTop: '16px', borderTop: '1px solid #000', paddingTop: '6px',
                    fontSize: '8px', color: '#333', fontStyle: 'italic', textAlign: 'center'
                }}>
                    This document is a property of Mega Soft. Unauthorized copying, reproduction, distribution, or alteration is strictly prohibited.
                </div>
            </div>
        );
    };

    // ── BRANCH MR GATEPASS DOCUMENT ──
    const GatepassDocument = () => (
        <div ref={printRef} style={{ width: '794px', minHeight: '1123px', padding: '30px 40px', fontFamily: 'Arial, sans-serif', fontSize: '11px', color: '#000', background: '#fff', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '6px' }}>
                <div style={{ fontWeight: 'bold', fontSize: '16px', letterSpacing: '1px' }}>MEGA SOFT HYGIENIC PRODUCT INC.</div>
                <div style={{ fontSize: '10px', marginTop: '2px' }}>38 - E Pleasant Drive, Pleasant View Subd., Libis, Caloocan City</div>
                <div style={{ fontSize: '10px', marginTop: '1px' }}>Telefax : 984-10-01</div>
                <div style={{ fontWeight: 'bold', fontSize: '13px', marginTop: '4px', letterSpacing: '2px' }}>GATEPASS</div>
            </div>
            <hr style={{ border: 'none', borderTop: '1.5px solid #000', margin: '6px 0 10px 0' }} />
            {getGINumber(selectedMR) !== '—' && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '6px' }}>
                    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', padding: '3px 10px', fontSize: '10px' }}>
                        <span style={{ fontWeight: 'bold', color: '#1e40af' }}>GI No.: </span>
                        <span style={{ fontWeight: 'bold', color: '#1e3a8a', fontFamily: 'monospace' }}>{getGINumber(selectedMR)}</span>
                    </div>
                </div>
            )}
            <div style={{ marginBottom: '4px' }}><span style={{ fontWeight: 'bold', fontSize: '12px' }}>DELIVERY TO</span></div>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '0' }}>
                <tbody>
                    <tr>
                        <td style={{ border: 'none', padding: '2px 4px', width: '110px', fontWeight: 'bold', fontSize: '10px' }}>CUSTOMER NAME</td>
                        <td style={{ border: 'none', padding: '2px 4px', fontSize: '10px' }}>:</td>
                        <td style={{ padding: '2px 4px', borderBottom: '1px solid #000', fontSize: '10px', width: '320px', fontWeight: 'bold' }}>{selectedMR?.branchSpecify || selectedMR?.departmentName || ''}</td>
                        <td style={{ border: 'none', padding: '2px 12px', fontWeight: 'bold', fontSize: '10px', textAlign: 'right', width: '80px' }}>WBREF:</td>
                        <td style={{ padding: '2px 4px', borderBottom: '1px solid #000', fontSize: '10px', minWidth: '120px' }}>{selectedMR?.gI_Number || ''}</td>
                    </tr>
                    <tr>
                        <td style={{ border: 'none', padding: '2px 4px', fontWeight: 'bold', fontSize: '10px' }}>CUSTOMER ADDRESS</td>
                        <td style={{ border: 'none', padding: '2px 4px', fontSize: '10px' }}>:</td>
                        <td colSpan={3} style={{ borderBottom: '1px solid #000', padding: '2px 4px', fontSize: '10px' }}>&nbsp;</td>
                    </tr>
                </tbody>
            </table>
            <div style={{ marginTop: '8px', marginBottom: '2px' }}><span style={{ fontWeight: 'bold', fontSize: '12px' }}>DELIVERY INSTRUCTION</span></div>
            <table style={{ width: '60%', borderCollapse: 'collapse', marginBottom: '4px' }}>
                <tbody>
                    {[{ label: 'DELIVERY DATE', value: formattedDate }, { label: 'EXPECTED ARRIVAL DATE', value: '' }, { label: 'TRUCK PLATE NO.', value: '' }].map((row, i) => (
                        <tr key={i}>
                            <td style={{ border: 'none', padding: '2px 4px', fontWeight: 'bold', fontSize: '10px', width: '160px' }}>{row.label}</td>
                            <td style={{ borderBottom: '1px solid #000', padding: '2px 8px', fontSize: '10px', minWidth: '160px' }}>{row.value}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div style={{ marginTop: '4px', marginBottom: '10px' }}>
                <span style={{ fontWeight: 'bold', fontSize: '10px' }}>Request by: </span>
                <span style={{ display: 'inline-block', borderBottom: '1px solid #000', minWidth: '200px', fontSize: '10px', paddingLeft: '8px' }}>{selectedMR?.requestedByName || ''}</span>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        {['CODE', 'ITEM DESCRIPTION', 'REMARKS', 'QTY', 'PACKAGING'].map((h, i) => (
                            <th key={i} style={{ border: '1px solid #000', padding: '5px 8px', textAlign: 'center', fontWeight: 'bold', fontSize: '10px', width: ['10%', '38%', '24%', '10%', '18%'][i] }}>{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {selectedMRDetails.length === 0 ? (
                        <tr><td colSpan={5} style={{ border: '1px solid #000', padding: '10px', textAlign: 'center', color: '#999' }}>No items found</td></tr>
                    ) : selectedMRDetails.map((item, idx) => (
                        <tr key={idx}>
                            <td style={{ border: '1px solid #000', padding: '4px 8px', textAlign: 'center', fontSize: '10px' }}>{item.itemCode || ''}</td>
                            <td style={{ border: '1px solid #000', padding: '4px 8px', textAlign: 'left', fontSize: '10px', textTransform: 'uppercase' }}>{item.itemDescription || item.itemCode}</td>
                            <td style={{ border: '1px solid #000', padding: '4px 8px', textAlign: 'center', fontSize: '10px' }}>{item.remarks || item.purpose || ''}</td>
                            <td style={{ border: '1px solid #000', padding: '4px 8px', textAlign: 'center', fontSize: '10px', fontWeight: 'bold' }}>{item.requestedQty}</td>
                            <td style={{ border: '1px solid #000', padding: '4px 8px', textAlign: 'center', fontSize: '10px', fontWeight: 'bold' }}>{item.uom?.toUpperCase() || ''}</td>
                        </tr>
                    ))}
                    {Array.from({ length: Math.max(0, 5 - selectedMRDetails.length) }).map((_, i) => (
                        <tr key={`empty-${i}`}>
                            {[...Array(5)].map((_, j) => <td key={j} style={{ border: '1px solid #000', padding: '4px 8px', height: '22px' }}>&nbsp;</td>)}
                        </tr>
                    ))}
                </tbody>
            </table>
            <div style={{ height: '24px' }} />
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                    <tr>
                        {[{ label: 'Prepared by:', name: selectedMR?.requestedByName }, { label: 'Endorsed To:', name: selectedMR?.approvedByName }, { label: 'Dispatch by:', name: selectedMR?.releasedByName }].map((sig, i) => (
                            <td key={i} style={{ width: '33%', padding: i === 0 ? '0 20px 0 0' : i === 1 ? '0 10px' : '0 0 0 20px', verticalAlign: 'top', border: 'none' }}>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontWeight: 'bold', fontSize: '10px', marginBottom: '28px' }}>{sig.label}</div>
                                    <div style={{ borderBottom: '1px solid #000', paddingBottom: '2px', fontSize: '11px', fontWeight: 'bold', minHeight: '20px' }}>{sig.name || ''}</div>
                                    {i === 2 && <div style={{ fontSize: '9px', color: '#555', marginTop: '2px' }}>Signature over Printed Name/Date</div>}
                                </div>
                            </td>
                        ))}
                    </tr>
                </tbody>
            </table>
            <div style={{ marginTop: '20px' }}>
                <div style={{ fontWeight: 'bold', fontSize: '11px', marginBottom: '8px' }}>CUSTOMERS ACKNOWLEDGMENT:</div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                        <tr>
                            <td style={{ border: 'none', padding: '2px 4px', width: '180px', fontSize: '10px' }}>Customer Representative</td>
                            <td style={{ border: 'none', padding: '2px 4px', fontSize: '10px' }}>:</td>
                            <td style={{ borderBottom: '1px solid #000', padding: '2px 8px', fontSize: '10px', width: '260px' }}>&nbsp;</td>
                            <td style={{ border: 'none', padding: '2px 8px', textAlign: 'center', fontSize: '9px', color: '#555', width: '220px' }}>Signature over Printed Name/Date</td>
                        </tr>
                        <tr>
                            <td style={{ border: 'none', padding: '4px 4px', fontSize: '10px' }}>Status of Delivered Items</td>
                            <td style={{ border: 'none', padding: '4px 4px', fontSize: '10px' }}>:</td>
                            <td style={{ borderBottom: '1px solid #000', padding: '4px 8px', fontSize: '10px' }}>&nbsp;</td>
                            <td style={{ border: 'none' }}>&nbsp;</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div style={{ marginTop: '18px', fontSize: '9px', color: '#333' }}>2-Customer Copy/1-Logistics Copy/-1File Copy</div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100 p-6">
            <div className="max-w-8xl mx-auto">

                {/* PAGE HEADER */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-7 mb-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="bg-gradient-to-br from-blue-700 to-blue-900 p-4 rounded-xl shadow-lg shadow-blue-200">
                                <FileText className="w-8 h-8 text-white" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Gatepass</h1>
                                <p className="text-slate-500 mt-1 text-sm">Branch Material Requests — Gatepass Documents</p>
                            </div>
                        </div>
                        <button onClick={() => { fetchBranchMRs(); fetchSavedGatepasses(); }}
                            className="flex items-center gap-2 px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold text-sm transition-all">
                            <RefreshCw className="w-4 h-4" /> Refresh
                        </button>
                    </div>
                </div>

                {/* TABS */}
                <div className="flex gap-2 mb-6">
                    {[
                        { key: 'list', icon: <FileText className="w-4 h-4" />, label: 'Branch MR List' },
                        { key: 'saved', icon: <List className="w-4 h-4" />, label: `Gatepass List (${savedGatepasses.length})` },
                        { key: 'create', icon: <FilePlus className="w-4 h-4" />, label: 'Manual Create Gatepass' },
                    ].map(tab => (
                        <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all shadow-sm ${activeTab === tab.key
                                ? 'bg-gradient-to-r from-blue-700 to-blue-800 text-white shadow-blue-200 shadow-md'
                                : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                                }`}>
                            {tab.icon}{tab.label}
                        </button>
                    ))}
                </div>

                {/* TAB 1: BRANCH MR LIST */}
                {activeTab === 'list' && (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                            {[
                                { label: 'Total Branch MRs', value: branchMRs.length, color: 'text-blue-700', bg: 'bg-blue-50' },
                                { label: 'Draft', value: branchMRs.filter(m => m.status === 0).length, color: 'text-gray-700', bg: 'bg-gray-50' },
                                { label: 'For Approval', value: branchMRs.filter(m => m.status === 1).length, color: 'text-yellow-700', bg: 'bg-yellow-50' },
                                { label: 'Approved / Released', value: branchMRs.filter(m => m.status >= 2).length, color: 'text-green-700', bg: 'bg-green-50' },
                            ].map((stat, i) => (
                                <div key={i} className={`${stat.bg} rounded-xl p-5 border border-white shadow-sm`}>
                                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{stat.label}</p>
                                    <p className={`text-3xl font-bold ${stat.color}`}>{stat.value}</p>
                                </div>
                            ))}
                        </div>
                        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                            <div className="p-5 border-b border-slate-100 bg-slate-50">
                                <div className="relative max-w-md">
                                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                                    <input type="text" placeholder="Search MR No, Control No, GI No, Branch..."
                                        value={searchText} onChange={e => setSearchText(e.target.value)}
                                        className="w-full pl-11 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white transition-all" />
                                </div>
                            </div>
                            {loading ? (
                                <div className="flex items-center justify-center py-24">
                                    <Loader2 className="w-10 h-10 text-blue-600 animate-spin mx-auto mb-3" />
                                </div>
                            ) : error ? (
                                <div className="flex items-center justify-center py-24">
                                    <div className="text-center">
                                        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                                        <p className="text-slate-700 font-semibold">{error}</p>
                                        <button onClick={fetchBranchMRs} className="mt-4 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700">Retry</button>
                                    </div>
                                </div>
                            ) : currentMRs.length === 0 ? (
                                <div className="flex items-center justify-center py-24">
                                    <div className="text-center">
                                        <Building2 className="w-14 h-14 text-slate-300 mx-auto mb-3" />
                                        <p className="text-slate-500 font-semibold">No Branch Gatepasses found</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="bg-gradient-to-r from-blue-700 to-blue-800 text-white text-xs uppercase tracking-wider">
                                                {['MR No.', 'GI No.', 'Control No.', 'Date', 'Branch', 'Requested By', 'Status', 'Action'].map((h, i) => (
                                                    <th key={i} className={`px-5 py-4 font-bold ${i === 7 ? 'text-center' : 'text-left'}`}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {currentMRs.map((mr, idx) => {
                                                const status = statusLabels[mr.status] || statusLabels[0];
                                                return (
                                                    <tr key={mr.id} className={`hover:bg-blue-50/50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}>
                                                        <td className="px-5 py-3.5"><span className="font-mono font-bold text-blue-900 text-sm">{mr.mrNo}</span></td>
                                                        <td className="px-5 py-3.5"><span className="font-mono font-bold text-blue-900 text-sm">{mr.gI_Number}</span></td>
                                                        <td className="px-5 py-3.5"><span className="font-mono text-slate-600 text-sm">{mr.controlNo || '—'}</span></td>
                                                        <td className="px-5 py-3.5 text-sm text-slate-600">
                                                            {new Date(mr.mrDate).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}
                                                        </td>
                                                        <td className="px-5 py-3.5">
                                                            <div className="flex items-center gap-2">
                                                                <Building2 className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                                                <span className="text-sm font-semibold text-slate-800">{mr.branchSpecify || mr.departmentName || 'Branch'}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-5 py-3.5 text-sm text-slate-700">{mr.requestedByName}</td>
                                                        <td className="px-5 py-3.5">
                                                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${status.color}`}>{status.label}</span>
                                                        </td>
                                                        <td className="px-5 py-3.5 text-center">
                                                            <button onClick={() => handleViewGatepass(mr)}
                                                                className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold transition-all shadow-sm hover:shadow-md">
                                                                <Printer className="w-3.5 h-3.5" /> View Gatepass
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            {totalPages > 1 && (
                                <div className="px-5 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
                                    <p className="text-sm text-slate-500">Showing {startIdx + 1}–{Math.min(startIdx + itemsPerPage, filteredMRs.length)} of {filteredMRs.length}</p>
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                                            className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-100"><ChevronLeft className="w-4 h-4" /></button>
                                        <span className="text-sm font-semibold px-2">{currentPage} / {totalPages}</span>
                                        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                                            className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-100"><ChevronRight className="w-4 h-4" /></button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {/* TAB 2: SAVED GATEPASS LIST */}
                {activeTab === 'saved' && (
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="p-5 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                            <div className="relative max-w-md flex-1">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                                <input type="text" placeholder="Search by WBREF, customer, prepared by..."
                                    value={savedSearch} onChange={e => { setSavedSearch(e.target.value); setSavedPage(1); }}
                                    className="w-full pl-11 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                            </div>
                            <button onClick={fetchSavedGatepasses}
                                className="ml-3 flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold text-sm transition-all">
                                <RefreshCw className="w-4 h-4" />
                            </button>
                        </div>
                        {loadingSaved ? (
                            <div className="flex items-center justify-center py-24">
                                <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
                            </div>
                        ) : currentSaved.length === 0 ? (
                            <div className="flex items-center justify-center py-24">
                                <div className="text-center">
                                    <FileText className="w-14 h-14 text-slate-300 mx-auto mb-3" />
                                    <p className="text-slate-500 font-semibold">No saved gatepasses yet</p>
                                    <p className="text-slate-400 text-sm mt-1">Create one from the Manual Create tab</p>
                                </div>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-gradient-to-r from-blue-700 to-blue-800 text-white text-xs uppercase tracking-wider">
                                            {['#', 'Control No.', 'Customer', 'Delivery Date', 'Prepared By', 'Status', 'Items', 'Action'].map((h, i) => (
                                                <th key={i} className={`px-5 py-4 font-bold ${i === 7 ? 'text-center' : 'text-left'}`}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {currentSaved.map((gp, idx) => (
                                            <tr key={gp.id} className={`hover:bg-blue-50/50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}>
                                                <td className="px-5 py-3.5 text-sm text-slate-500 font-mono">{gp.id}</td>
                                                <td className="px-5 py-3.5"><span className="font-mono font-bold text-blue-900 text-sm">{gp.wbRef || '—'}</span></td>
                                                <td className="px-5 py-3.5 text-sm font-semibold text-slate-800">{gp.customerName || '—'}</td>
                                                <td className="px-5 py-3.5 text-sm text-slate-600">
                                                    {gp.deliveryDate ? new Date(gp.deliveryDate).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '—'}
                                                </td>
                                                <td className="px-5 py-3.5 text-sm text-slate-700">{gp.preparedBy || '—'}</td>
                                                <td className="px-5 py-3.5">
                                                    <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-700">
                                                        {gp.status || 'Draft'}
                                                    </span>
                                                </td>
                                                <td className="px-5 py-3.5 text-sm text-slate-600">
                                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-bold">
                                                        {gp.items?.length || 0} item(s)
                                                    </span>
                                                </td>
                                                <td className="px-5 py-3.5 text-center">
                                                    <button onClick={() => { setSelectedSaved(gp); setShowSavedModal(true); }}
                                                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold transition-all shadow-sm hover:shadow-md">
                                                        <Printer className="w-3.5 h-3.5" /> View / Print
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {savedTotalPages > 1 && (
                            <div className="px-5 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
                                <p className="text-sm text-slate-500">
                                    Showing {savedStartIdx + 1}–{Math.min(savedStartIdx + itemsPerPage, filteredSaved.length)} of {filteredSaved.length}
                                </p>
                                <div className="flex items-center gap-2">
                                    <button onClick={() => setSavedPage(p => Math.max(1, p - 1))} disabled={savedPage === 1}
                                        className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-100"><ChevronLeft className="w-4 h-4" /></button>
                                    <span className="text-sm font-semibold px-2">{savedPage} / {savedTotalPages}</span>
                                    <button onClick={() => setSavedPage(p => Math.min(savedTotalPages, p + 1))} disabled={savedPage === savedTotalPages}
                                        className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-100"><ChevronRight className="w-4 h-4" /></button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* TAB 3: MANUAL CREATE */}
                {activeTab === 'create' && (
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
                        <h2 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
                            <FilePlus className="w-5 h-5 text-blue-700" />
                            Manual Gatepass
                        </h2>

                        {/* DELIVERY TO */}
                        <section className="mb-6">
                            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Delivery To</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Customer Name</label>
                                    <input name="customerName" value={manualForm.customerName} onChange={handleManualChange}
                                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                        placeholder="Customer / Branch name" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Attention To</label>
                                    <input name="customerAddress" value={manualForm.customerAddress} onChange={handleManualChange}
                                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                        placeholder="Address" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Control No.</label>
                                    <div className="flex gap-2">
                                        <input name="wbRef" value={manualForm.wbRef}
                                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none font-mono"
                                            readOnly />
                                        <button onClick={() => setManualForm(prev => ({ ...prev, wbRef: generateWbRef() }))}
                                            className="px-3 py-2 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-lg transition-all"
                                            title="Regenerate WBREF">
                                            <RefreshCw className="w-4 h-4 text-slate-600" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* DELIVERY INSTRUCTION */}
                        <section className="mb-6">
                            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Delivery Instruction</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Delivery Date</label>
                                    <input type="date" name="deliveryDate" value={manualForm.deliveryDate} onChange={handleManualChange}
                                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Expected Arrival Date</label>
                                    <input type="date" name="expectedArrivalDate" value={manualForm.expectedArrivalDate} onChange={handleManualChange}
                                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Truck Plate No.</label>
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                        <input
                                            type="text"
                                            placeholder="Search truck plate..."
                                            value={truckSearch}
                                            onChange={e => setTruckSearch(e.target.value)}
                                            onFocus={() => setTruckDropdownOpen(true)}
                                            onBlur={() => setTimeout(() => setTruckDropdownOpen(false), 200)}
                                            className="w-full pl-9 pr-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                        />
                                        {truckDropdownOpen && (
                                            <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                                                {trucks
                                                    .filter(t =>
                                                        t.truckPlate?.toLowerCase().includes(truckSearch.toLowerCase()) ||
                                                        t.model?.toLowerCase().includes(truckSearch.toLowerCase())
                                                    )
                                                    .map(t => (
                                                        <div key={t.id}
                                                            onMouseDown={() => {
                                                                handleTruckSelect({ target: { value: t.id } });
                                                                setTruckSearch(`${t.truckPlate} — ${t.model}`);
                                                                setTruckDropdownOpen(false);
                                                            }}
                                                            className="px-4 py-2.5 text-sm hover:bg-blue-50 cursor-pointer border-b border-slate-100 last:border-0">
                                                            <span className="font-bold text-blue-700">{t.truckPlate}</span>
                                                            <span className="text-slate-500 ml-2">— {t.model}</span>
                                                        </div>
                                                    ))
                                                }
                                                {trucks.filter(t =>
                                                    t.truckPlate?.toLowerCase().includes(truckSearch.toLowerCase()) ||
                                                    t.model?.toLowerCase().includes(truckSearch.toLowerCase())
                                                ).length === 0 && (
                                                        <div className="px-4 py-3 text-sm text-slate-400 text-center">No trucks found</div>
                                                    )}
                                            </div>
                                        )}
                                    </div>
                                    {manualForm.truckPlateNo && (
                                        <p className="mt-1 text-xs font-semibold text-green-700">✓ {manualForm.truckPlateNo}</p>
                                    )}
                                </div>
                            </div>
                            <div className="mt-4 max-w-sm">
                                <label className="block text-sm font-semibold text-slate-700 mb-1">Requested By</label>
                                <input name="requestedBy" value={manualForm.requestedBy}
                                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm outline-none bg-slate-50" readOnly />
                            </div>
                        </section>

                        {/* ITEMS */}
                        <section className="mb-6">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Items</h3>
                                <button onClick={addItem}
                                    disabled={manualForm.items.length >= 20}
                                    className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold transition-all
        ${manualForm.items.length >= 20
                                            ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                                        }`}>
                                    <Plus className="w-3.5 h-3.5" />
                                    Add Item {manualForm.items.length >= 20 ? '(Max)' : `(${manualForm.items.length}/20)`}
                                </button>
                            </div>
                            <div className="overflow-x-auto rounded-xl border border-slate-200">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-gradient-to-r from-blue-700 to-blue-800 text-white text-xs">
                                            {['Qty', 'Units', 'Code (optional)', 'Item Description', 'Remarks', 'Packaging', ''].map((h, i) => (
                                                <th key={i} className="px-4 py-3 text-left font-bold">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {manualForm.items.map((item, idx) => (
                                            <tr key={idx} className="hover:bg-slate-50">
                                                <td className="px-3 py-2">
                                                    <input type="number" value={item.qty} onChange={e => handleItemChange(idx, 'qty', e.target.value)}
                                                        className="w-16 px-2 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 outline-none"
                                                        placeholder="0" />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <input value={item.unit} onChange={e => handleItemChange(idx, 'unit', e.target.value)}
                                                        className="w-24 px-2 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 outline-none"
                                                        placeholder="e.g. PC" />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <input value={item.code} onChange={e => handleItemChange(idx, 'code', e.target.value)}
                                                        className="w-20 px-2 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 outline-none"
                                                        placeholder="Optional" />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <input value={item.description} onChange={e => handleItemChange(idx, 'description', e.target.value)}
                                                        className="w-56 px-2 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 outline-none"
                                                        placeholder="Description" />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <input value={item.remarks} onChange={e => handleItemChange(idx, 'remarks', e.target.value)}
                                                        className="w-36 px-2 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 outline-none"
                                                        placeholder="Remarks" />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <input value={item.packaging} onChange={e => handleItemChange(idx, 'packaging', e.target.value)}
                                                        className="w-28 px-2 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 outline-none"
                                                        placeholder="e.g. BOX" />
                                                </td>
                                                <td className="px-3 py-2">
                                                    {manualForm.items.length > 1 && (
                                                        <button onClick={() => removeItem(idx)}
                                                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all">
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>

                        {/* APPROVALS */}
                        <section className="mb-8">
                            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Approvals & Signatures</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {[
                                    { label: 'Prepared By', name: 'preparedBy' },
                                    { label: 'Endorsed To:', name: 'verifiedBy' },
                                    { label: 'Received By:', name: 'dispatchBy' },
                                ].map((sig) => (
                                    <div key={sig.name}>
                                        <label className="block text-sm font-semibold text-slate-700 mb-1">{sig.label}</label>
                                        {/* FIX: Dispatch By = manual text input, others = dropdown */}
                                        {sig.name === 'dispatchBy' ? (
                                            <input
                                                value={manualForm.dispatchBy}
                                                onChange={e => setManualForm(prev => ({ ...prev, dispatchBy: e.target.value }))}
                                                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                                placeholder="Enter dispatcher name" />
                                        ) : (
                                            <select onChange={(e) => {
                                                const user = users.find(u => u.id === parseInt(e.target.value));
                                                setManualForm(prev => ({
                                                    ...prev,
                                                    [sig.name]: user ? `${user.firstName} ${user.lastName}` : ''
                                                }));
                                            }} className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white" defaultValue="">
                                                <option value="">Select User</option>
                                                {users.map(u => (
                                                    <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>
                                                ))}
                                            </select>
                                        )}
                                        {manualForm[sig.name] && (
                                            <p className="mt-1 text-xs font-semibold text-green-700">✓ {manualForm[sig.name]}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* ACTION BUTTONS */}
                        {/* NEW */}
                        <div className="flex justify-between items-center">
                            {/* Left — Draft buttons */}
                            <div className="flex gap-2">
                                <button onClick={saveDraft}
                                    className="flex items-center gap-2 px-5 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-bold text-sm transition-all shadow">
                                    <Save className="w-4 h-4" /> Save Draft
                                </button>

                                <button
                                    onClick={() => drafts.length > 0 && setShowDraftModal(true)}
                                    disabled={drafts.length === 0}
                                    className={`flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-sm transition-all shadow
                ${drafts.length > 0
                                            ? 'bg-slate-600 hover:bg-slate-700 text-white cursor-pointer'
                                            : 'bg-slate-200 text-slate-400 cursor-not-allowed opacity-50'
                                        }`}>
                                    <List className="w-4 h-4" />
                                    Load Draft
                                    {drafts.length > 0 && (
                                        <span className="bg-amber-400 text-white text-xs px-1.5 py-0.5 rounded-full">
                                            {drafts.length}
                                        </span>
                                    )}
                                </button>
                            </div>

                            {/* Right — Export + Save */}
                            <div className="flex gap-3">
                                <button onClick={exportManualToPDF}
                                    className="flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-blue-700 to-blue-800 hover:from-blue-800 hover:to-blue-900 text-white rounded-xl font-bold text-sm transition-all shadow-lg hover:shadow-xl hover:scale-105">
                                    <Printer className="w-5 h-5" /> Export to PDF
                                </button>
                                <button onClick={saveManualGatepass}
                                    className="flex items-center gap-2 px-6 py-3.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold text-sm transition-all shadow-lg">
                                    <Save className="w-5 h-5" /> Save Gatepass
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {/* DRAFT PREVIEW MODAL */}
            {showDraftModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden">
                        <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Save className="w-5 h-5" />
                                <div>
                                    <p className="font-bold text-lg">Saved Drafts</p>
                                    <p className="text-amber-100 text-xs">{drafts.length} draft(s) — click a row to load</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button onClick={clearAllDrafts}
                                    className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs font-bold transition-all">
                                    Clear All
                                </button>
                                <button onClick={() => setShowDraftModal(false)}
                                    className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-all">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100">
                            {drafts.map((draft) => (
                                <div key={draft.id}
                                    onClick={() => loadDraft(draft)}
                                    className="flex items-center gap-4 px-6 py-4 hover:bg-amber-50 cursor-pointer transition-colors group">
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-slate-800 text-sm truncate">{draft.name}</p>
                                        <p className="text-xs text-slate-400 mt-0.5">
                                            {new Date(draft.savedAt).toLocaleString()} •
                                            {draft.form.items?.filter(i => i.description || i.qty).length || 0} item(s) •
                                            WB: <span className="font-mono">{draft.form.wbRef}</span>
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); deleteDraft(draft.id); }}
                                            className="p-1.5 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-all">
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                        <span className="text-xs font-bold text-amber-600 bg-amber-100 px-3 py-1 rounded-lg">Load →</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end">
                            <button onClick={() => setShowDraftModal(false)}
                                className="px-5 py-2.5 bg-white border border-slate-300 text-slate-700 rounded-xl font-bold text-sm hover:bg-slate-100 transition-all">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* BRANCH MR GATEPASS MODAL */}
            {showGatepass && selectedMR && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
                        <div className="bg-gradient-to-r from-blue-800 to-blue-900 text-white px-6 py-4 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <FileText className="w-5 h-5" />
                                <div>
                                    <p className="font-bold text-lg">Gatepass — {selectedMR.mrNo}</p>
                                    <div className="flex items-center gap-3 mt-0.5">
                                        <p className="text-blue-200 text-xs">{selectedMR.branchSpecify || selectedMR.departmentName}</p>
                                        {getGINumber(selectedMR) !== '—' && (
                                            <span className="bg-white/20 text-white text-xs font-mono font-bold px-2 py-0.5 rounded-md">
                                                GI: {getGINumber(selectedMR)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <button onClick={handlePrint}
                                    className="flex items-center gap-2 px-5 py-2 bg-white text-blue-900 rounded-xl font-bold text-sm hover:bg-blue-50 transition-all shadow">
                                    <Printer className="w-4 h-4" /> Print
                                </button>
                                <button onClick={() => { setShowGatepass(false); setSelectedMR(null); setSelectedMRDetails([]); }}
                                    className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-all">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 bg-gray-100">
                            {loadingDetails ? (
                                <div className="flex items-center justify-center py-20">
                                    <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                                </div>
                            ) : (
                                <GatepassDocument />
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* SAVED GATEPASS MODAL */}
            {showSavedModal && selectedSaved && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
                        <div className="bg-gradient-to-r from-blue-800 to-blue-900 text-white px-6 py-4 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <FileText className="w-5 h-5" />
                                <div>
                                    <p className="font-bold text-lg">Gatepass — {selectedSaved.wbRef}</p>
                                    <p className="text-blue-200 text-xs">{selectedSaved.customerName}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <button onClick={handlePrintSaved}
                                    className="flex items-center gap-2 px-5 py-2 bg-white text-blue-900 rounded-xl font-bold text-sm hover:bg-blue-50 transition-all shadow">
                                    <Printer className="w-4 h-4" /> Print
                                </button>
                                <button onClick={() => { setShowSavedModal(false); setSelectedSaved(null); }}
                                    className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-all">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 bg-gray-100">
                            <SavedGatepassDocument gp={selectedSaved} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}